import { useMemo, useState } from 'react';
import type { Activity, ItemResponse, ListResponse, UserSettings } from '@jarvis/shared';
import { DELETE_ACCOUNT_PHRASE, DELETE_ALL_PHRASE } from '@jarvis/shared/constants';
import { authStore } from '../lib/auth';
import { Icon } from '../components/Icon';
import { toast } from '../components/toast';
import { ErrorState, Skeleton } from '../components/ui';
import { api, downloadExport } from '../lib/api';
import { fmt } from '../lib/format';
import { useLive } from '../lib/useLive';

const errMsg = (e: unknown) => (e instanceof Error ? e.message : 'Algo deu errado.');

const ACTION_LABEL: Record<string, string> = {
  'project.created': 'Projeto criado',
  'project.updated': 'Projeto editado',
  'project.archived': 'Projeto arquivado',
  'project.restored': 'Projeto restaurado',
  'project.deleted': 'Projeto excluído',
  'task.created': 'Tarefa adicionada',
  'task.updated': 'Tarefa alterada',
  'task.deleted': 'Tarefa excluída',
  'routine.created': 'Item de rotina adicionado',
  'routine.updated': 'Item de rotina alterado',
  'routine.deleted': 'Item de rotina removido',
  'settings.updated': 'Configurações alteradas',
  'data.exported': 'Dados exportados',
  'memory.created': 'Memória guardada',
  'memory.updated': 'Memória editada',
  'memory.deleted': 'Memória apagada',
  'memory.auto': 'Jarvis aprendeu algo sobre você',
  'memory.reviewed': 'Memória revisada',
  'chat.cleared': 'Histórico da conversa apagado'
};

const PREFERRED_ZONES = ['America/Sao_Paulo', 'America/Manaus', 'America/Belem', 'America/Fortaleza', 'America/Recife', 'America/Cuiaba', 'America/Rio_Branco', 'America/Noronha'];

export function TimezoneField({ onSaved }: { onSaved?: () => void }) {
  const { state, set, reload } = useLive('/api/settings', (b: ItemResponse<UserSettings>) => b.item);
  const [busy, setBusy] = useState(false);
  const zones = useMemo(() => {
    const all = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : PREFERRED_ZONES;
    return [...PREFERRED_ZONES.filter((z) => all.includes(z)), ...all.filter((z) => !PREFERRED_ZONES.includes(z))];
  }, []);

  if (state.status === 'loading') return <Skeleton kind="line" />;
  if (state.status === 'error') return <ErrorState error={state.error} onRetry={reload} compact />;

  const change = async (timezone: string) => {
    setBusy(true);
    try {
      const r = await api.patch<ItemResponse<UserSettings>>('/api/settings', { timezone });
      set(() => r.item);
      onSaved?.();
      toast('Fuso horário salvo.', 'check');
    } catch (err) {
      toast(errMsg(err), 'alert');
    } finally {
      setBusy(false);
    }
  };

  const current = zones.includes(state.data.timezone) ? zones : [state.data.timezone, ...zones];
  return (
    <div className="field">
      <label htmlFor="timezone">Fuso horário</label>
      <select className="input" id="timezone" value={state.data.timezone} disabled={busy} onChange={(e) => change(e.target.value)}>
        {current.map((z) => <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>)}
      </select>
      <span className="hint">Salvo no servidor. Define o que é "hoje" no briefing e nos lembretes.</span>
    </div>
  );
}

export function ActivityList({ refreshKey }: { refreshKey: number }) {
  const { state, reload } = useLive(`/api/activity?limit=8&k=${refreshKey}`, (b: ListResponse<Activity>) => b.items);
  if (state.status === 'loading') return <Skeleton kind="line" />;
  if (state.status === 'error') return <ErrorState error={state.error} onRetry={reload} compact />;
  if (!state.data.length) return <p className="row-sub">Nenhuma atividade registrada.</p>;
  return (
    <ul className="list activity-list">
      {state.data.map((a) => (
        <li key={a.id} className="row">
          <span className="row-meta">{fmt.relative(a.at)}</span>
          <div className="row-main">{ACTION_LABEL[a.action] ?? a.action}</div>
        </li>
      ))}
    </ul>
  );
}

export function DeleteAccount() {
  const [open, setOpen] = useState(false);
  const [phrase, setPhrase] = useState('');
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await api.post('/api/account/delete', { confirm: phrase });
      authStore.set({ mode: 'supabase', status: 'signed_out' });
    } catch (err) {
      toast(errMsg(err), 'alert');
      setBusy(false);
    }
  };
  if (!open) {
    return <div className="card-foot"><button type="button" className="btn btn-sm btn-danger-ghost" onClick={() => setOpen(true)}><Icon name="trash" />Excluir minha conta</button></div>;
  }
  return (
    <div className="danger-zone" role="group" aria-labelledby="da-title">
      <p id="da-title"><strong>Excluir sua conta?</strong> O login é removido e todos os dados são apagados do banco. Não há como desfazer. Se quiser uma cópia, exporte antes.</p>
      <label htmlFor="da-phrase">Para confirmar, digite <span className="mono">{DELETE_ACCOUNT_PHRASE}</span></label>
      <input id="da-phrase" className="input" autoComplete="off" value={phrase} onChange={(e) => setPhrase(e.target.value)} />
      <div className="chips">
        <button type="button" className="btn btn-danger" disabled={phrase !== DELETE_ACCOUNT_PHRASE || busy} onClick={run}>{busy ? 'Excluindo…' : 'Excluir conta'}</button>
        <button type="button" className="btn btn-ghost" onClick={() => { setOpen(false); setPhrase(''); }}>Cancelar</button>
      </div>
    </div>
  );
}

export function DataControls({ onChanged }: { onChanged: () => void }) {
  const [exporting, setExporting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [phrase, setPhrase] = useState('');
  const [deleting, setDeleting] = useState(false);

  const exportData = async () => {
    setExporting(true);
    try {
      const name = await downloadExport();
      toast(`Arquivo ${name} baixado.`, 'check');
      onChanged();
    } catch (err) {
      toast(errMsg(err), 'alert');
    } finally {
      setExporting(false);
    }
  };

  const deleteAll = async () => {
    setDeleting(true);
    try {
      const r = await api.post<{ deleted: { projects: number; routine: number; activity: number } }>('/api/account/delete-all', { confirm: phrase });
      toast(`Apagado: ${r.deleted.projects} projeto(s), ${r.deleted.routine} item(ns) de rotina e o registro de atividade.`, 'trash');
      setConfirmOpen(false);
      setPhrase('');
      onChanged();
    } catch (err) {
      toast(errMsg(err), 'alert');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="card-foot">
        <button type="button" className="btn btn-sm" onClick={exportData} disabled={exporting}><Icon name="file" />{exporting ? 'Exportando…' : 'Exportar meus dados (JSON)'}</button>
        {!confirmOpen && <button type="button" className="btn btn-sm btn-danger-ghost" onClick={() => setConfirmOpen(true)}><Icon name="trash" />Apagar todos os meus dados</button>}
      </div>
      {confirmOpen && (
        <div className="danger-zone" role="group" aria-labelledby="dz-title">
          <p id="dz-title"><strong>Apagar todos os seus dados?</strong> Projetos, tarefas, rotina e registro de atividade serão excluídos do banco, e o fuso volta ao padrão. Não há como desfazer. Se quiser uma cópia, exporte antes.</p>
          <label htmlFor="dz-phrase">Para confirmar, digite <span className="mono">{DELETE_ALL_PHRASE}</span></label>
          <input id="dz-phrase" className="input" autoComplete="off" value={phrase} onChange={(e) => setPhrase(e.target.value)} />
          <div className="chips">
            <button type="button" className="btn btn-danger" disabled={phrase !== DELETE_ALL_PHRASE || deleting} onClick={deleteAll}>{deleting ? 'Apagando…' : 'Apagar tudo'}</button>
            <button type="button" className="btn btn-ghost" onClick={() => { setConfirmOpen(false); setPhrase(''); }}>Cancelar</button>
          </div>
        </div>
      )}
    </>
  );
}
